import { SubmissionStatus } from '@bp/bp-graphql-types';
import { EditIcon, EmptyState, LazyLoader, SendIcon, UpdateIcon } from '@bp/ui-components';
import { BpActionButton, BpCard } from 'components/BpCard/BpCard';
import { FileListDownloader } from 'components/Files/components/FileList/FileListDownloader';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import styles from './SubmissionAnswerCard.module.scss';

type SubmissionAnswerCardStudentProps = {
  assignmentUuid: string;
  loading: boolean;
  onEdit: () => void;
  onRequestEdit: () => void;
  onSubmit: () => void;
};

export const SubmissionAnswerCardStudent = ({
  assignmentUuid,
  loading,
  onEdit,
  onRequestEdit,
  onSubmit,
}: SubmissionAnswerCardStudentProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const { courseUuid } = useRequireParams(['courseUuid']);

  const { submissions } = useSubmissions({ courseUuid, assignmentUuid });

  const submission = submissions.find((s) => s.assignment.uuid === assignmentUuid && s.owner.uuid === profileUuid);
  if (!submission) throw new Error('Submission not found');

  const material = submission.material;

  return (
    <BpCard
      className={styles['submission-answer-card-student']}
      noPadding={material.fileEntries.length > 0}
      alwaysCollapsible
      header={{
        headline: t('submissions.myAnswer'),
        subHeadline: submission.status === SubmissionStatus.Draft ? t('common.draft') : undefined,
        showSubHeadlineAsChip: true,
        actions: [
          ...(submission.status === SubmissionStatus.New || submission.status === SubmissionStatus.Done
            ? [
                {
                  text: t('submissions.requestToFeedback'),
                  hierarchy: 'tertiary',
                  icon: <UpdateIcon />,
                  callback: () => onRequestEdit(),
                } as BpActionButton,
              ]
            : []),
          ...(submission.isEditable
            ? [
                {
                  text: t('common.edit'),
                  hierarchy: 'tertiary',
                  icon: <EditIcon />,
                  callback: () => onEdit(),
                } as BpActionButton,
              ]
            : []),
          ...(submission.isEditable && submission.status === SubmissionStatus.Draft
            ? [
                {
                  text: t('common.send'),
                  hierarchy: 'tertiary',
                  icon: <SendIcon />,
                  callback: () => onSubmit(),
                } as BpActionButton,
              ]
            : []),
        ],
      }}
    >
      {loading && <LazyLoader className={styles.loader} />}

      {!material || (!material.text && material.fileEntries.length === 0) ? (
        <EmptyState hideIcon title={t('submissions.noAnswers')} subtitle={t('submissions.noAnswersHint')} padding='l' />
      ) : (
        <>
          {!material.text ? (
            <EmptyState hideIcon subtitle={t('submissions.noAnswerText')} padding='l' />
          ) : (
            <TipTapTextExtender
              uuid={material.uuid}
              content={material.text}
              padding={material.fileEntries.length === 0 ? 'none' : 'default'}
            />
          )}
          {material.fileEntries && material.fileEntries.length > 0 && (
            <FileListDownloader files={material.fileEntries} className='mt-7' />
          )}
        </>
      )}
    </BpCard>
  );
};
